/// <amd-module name="Artifacts/Nuget/services/NuGetDataService" />
import * as VSS_NuGet_Contracts from "Artifacts/Nuget/Contracts/NuGet";
import { NuGetPackagesBatchRequest } from "Artifacts/Nuget/Contracts/NuGet";
import * as NuGetPackageDetails from "Artifacts/Nuget/NuGetPackageDetails";
import { getNuGetClient, INuGetRestClient } from "Artifacts/Nuget/RestClient/NuGet";
import { INuGetRestApiUrlGeneratorService } from "Artifacts/Nuget/services/NuGetRestApiUrlGeneratorService";
// import { HubWebPageDataService } from "Feed/Legacy/DataServices/WebPageDataService";
import { BatchExecutor, IBatchIterator } from "Artifacts/Package/services/BatchHandling";
import { MinimalPackageDetails, UpstreamingBehavior } from "Artifacts/Root/types/PackagingShared";
import { IVssService, Services, VssService } from "VSS/Platform/Context";

export interface IFeedNuGetDataService extends IVssService {
  batchSize: number;
  updatePackageVersions(
    nuGetBatchRequest: NuGetPackagesBatchRequest,
    projectId: string,
    feedId: string,
  ): Promise<void>;
  updatePackageVersion(
    nugetPackageVersionDetails: NuGetPackageDetails.PackageVersionDetails,
    projectId: string,
    feedId: string,
    packageName: string,
    packageVersion: string,
  ): Promise<void>;
  getDownloadUrl(
    fileName: string,
    includeProject: boolean,
    publicAccessMapping: string,
  ): Promise<string>;
  getDownloadPackageVersionContentUrl(
    feedId: string,
    packageName: string,
    packageVersion: string,
    includeProject: boolean,
    publicAccessMapping: string,
  ): Promise<string>;
  batchDelete(
    projectId: string,
    feedId: string,
    iterator: IBatchIterator<MinimalPackageDetails[]>,
  ): Promise<void>;
  batchRestoreToFeed(
    projectId: string,
    feedId: string,
    iterator: IBatchIterator<MinimalPackageDetails[]>,
  ): Promise<void>;
  batchPermanentlyDelete(
    projectId: string,
    feedId: string,
    iterator: IBatchIterator<MinimalPackageDetails[]>,
  ): Promise<void>;
  batchPromotePackages(
    projectId: string,
    feedId: string,
    viewId: string,
    iterator: IBatchIterator<MinimalPackageDetails[]>,
  ): Promise<void[]>;
  getUpstreamingBehavior(
    feedId: string,
    packageName: string,
    projectId?: string,
  ): Promise<UpstreamingBehavior>;
  setUpstreamingBehavior(
    feedId: string,
    packageName: string,
    behavior: UpstreamingBehavior,
    projectId: string,
  ): Promise<void>;
}

class NuGetDataService extends VssService implements IFeedNuGetDataService {
  get NuGetRestClient(): INuGetRestClient {
    return getNuGetClient(this.pageContext);
  }

  get NuGetRestApiUrlGeneratorService(): INuGetRestApiUrlGeneratorService {
    return this.pageContext.getService<INuGetRestApiUrlGeneratorService>(
      "INuGetRestApiUrlGeneratorService",
    );
  }

  get batchSize() {
    return 100; // SGV TODO
    // return this.pageContext
    // 	.getService<HubWebPageDataService>("IFeedWebPageDataService")
    // 	.getBatchOperationPageSize();
  }

  public updatePackageVersions(
    nuGetBatchRequest: NuGetPackagesBatchRequest,
    projectId: string,
    feedId: string,
  ): Promise<void> {
    return this.NuGetRestClient.updatePackageVersions(nuGetBatchRequest, feedId, projectId);
  }

  public updatePackageVersion(
    nugetPackageVersionDetails: NuGetPackageDetails.PackageVersionDetails,
    projectId: string,
    feedId: string,
    packageName: string,
    packageVersion: string,
  ): Promise<void> {
    return this.NuGetRestClient.updatePackageVersion(
      nugetPackageVersionDetails,
      feedId,
      packageName,
      packageVersion,
      projectId,
    );
  }

  public getDownloadUrl(
    fileName: string,
    includeProject: boolean,
    publicAccessMapping: string,
  ): Promise<string> {
    const url: string = this.NuGetRestApiUrlGeneratorService.getDownloadClientUrl(
      fileName,
      includeProject,
      publicAccessMapping,
    );
    return Promise.resolve(url);
  }

  public getDownloadPackageVersionContentUrl(
    feedId: string,
    packageName: string,
    packageVersion: string,
    includeProject = false,
    publicAccessMapping: string,
  ): Promise<string> {
    const url: string = this.NuGetRestApiUrlGeneratorService.getDownloadPackageVersionContentUrl(
      feedId,
      packageName,
      packageVersion,
      includeProject,
      publicAccessMapping,
    );
    return Promise.resolve(url);
  }

  public async batchDelete(
    projectId: string,
    feedId: string,
    iterator: IBatchIterator<MinimalPackageDetails[]>,
  ): Promise<void> {
    await BatchExecutor.executeInParallel(
      iterator,
      async (packageDetails: MinimalPackageDetails[]) => {
        const batchRequest = <NuGetPackagesBatchRequest>{
          data: null,
          operation: VSS_NuGet_Contracts.NuGetBatchOperationType.Delete,
          packages: packageDetails,
        };
        await this.NuGetRestClient.updatePackageVersions(batchRequest, feedId, projectId);
      },
    );
  }

  public async batchRestoreToFeed(
    projectId: string,
    feedId: string,
    iterator: IBatchIterator<MinimalPackageDetails[]>,
  ): Promise<void> {
    await BatchExecutor.executeInParallel(
      iterator,
      async (packageDetails: MinimalPackageDetails[]) => {
        const batchRequest = <NuGetPackagesBatchRequest>{
          data: null,
          operation: VSS_NuGet_Contracts.NuGetBatchOperationType.RestoreToFeed,
          packages: packageDetails,
        };
        await this.NuGetRestClient.updateRecycleBinPackageVersions(batchRequest, feedId, projectId);
      },
    );
  }

  public async batchPermanentlyDelete(
    projectId: string,
    feedId: string,
    iterator: IBatchIterator<MinimalPackageDetails[]>,
  ): Promise<void> {
    await BatchExecutor.executeInParallel(
      iterator,
      async (packageDetails: MinimalPackageDetails[]) => {
        const batchRequest = <NuGetPackagesBatchRequest>{
          data: null,
          operation: VSS_NuGet_Contracts.NuGetBatchOperationType.PermanentDelete,
          packages: packageDetails,
        };
        await this.NuGetRestClient.updateRecycleBinPackageVersions(batchRequest, feedId, projectId);
      },
    );
  }

  public batchPromotePackages(
    projectId: string,
    feedId: string,
    viewId: string,
    iterator: IBatchIterator<MinimalPackageDetails[]>,
  ): Promise<void[]> {
    return BatchExecutor.executeInParallel(
      iterator,
      async (packageDetails: MinimalPackageDetails[]) => {
        const batchRequest = <NuGetPackagesBatchRequest>{
          data: { viewId },
          operation: VSS_NuGet_Contracts.NuGetBatchOperationType.Promote,
          packages: packageDetails,
        };
        await this.NuGetRestClient.updatePackageVersions(batchRequest, feedId, projectId);
      },
    );
  }

  public getUpstreamingBehavior(
    feedId: string,
    packageName: string,
    projectId: string,
  ): Promise<UpstreamingBehavior> {
    return this.NuGetRestClient.getUpstreamingBehavior(feedId, packageName, projectId);
  }

  public setUpstreamingBehavior(
    feedId: string,
    packageName: string,
    behavior: UpstreamingBehavior,
    projectId: string,
  ): Promise<void> {
    return this.NuGetRestClient.setUpstreamingBehavior(feedId, packageName, behavior, projectId);
  }
}

Services.add("IFeedNuGetDataService", { serviceFactory: NuGetDataService });
